import { Button, Input, Space, Typography, Upload } from "antd";
import type { UploadProps } from "antd";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog } from "zhihao-ui-react";

import type { ConfirmReceivePaymentForm, File as EntityFile, ReceivePayment } from "@zhihaoscm/entity";

import "./confirmPaymentDialog.scss";

type PaymentVoucherFile = Pick<EntityFile, "id" | "name" | "path">;

export interface ConfirmPaymentDialogProps {
    data?: ReceivePayment;
    open: boolean;
    uploadFile: (file: Blob) => Promise<PaymentVoucherFile>;
    onCancel: () => void;
    onSubmit: (data: ConfirmReceivePaymentForm) => Promise<void> | void;
}

const ACCEPTED_EXTENSIONS = [
    ".zip",
    ".rar",
    ".7z",
    ".aip",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".avif",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".pdf"
];

/** 对齐旧 Vue 确认付款弹窗，统一凭证、版本号和付款说明的提交契约。 */
export default function ConfirmPaymentDialog({
    data,
    open,
    uploadFile,
    onCancel,
    onSubmit
}: ConfirmPaymentDialogProps) {
    const [fileIds, setFileIds] = useState<number[]>([]);
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [paymentInstruction, setPaymentInstruction] = useState("");
    const [uploading, setUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!open) return;
        setFileIds(data?.paymentFileId || []);
        setFileNames([]);
        setPaymentInstruction(data?.paymentInstruction || "");
        setErrorMessage("");
    }, [data, open]);

    /** 复用旧 Vue file-list 的数量、大小和扩展名约束。 */
    const beforeUpload: UploadProps["beforeUpload"] = file => {
        const fileName = file.name.toLowerCase();
        if (fileIds.length >= 10) {
            setErrorMessage("付款凭证最多上传10个文件");
            return Upload.LIST_IGNORE;
        }
        if (file.size > 50 * 1024 * 1024) {
            setErrorMessage("付款凭证单个文件不能超过50M");
            return Upload.LIST_IGNORE;
        }
        if (!ACCEPTED_EXTENSIONS.some(extension => fileName.endsWith(extension))) {
            setErrorMessage("上传失败，仅支持图片、文档和压缩包文件");
            return Upload.LIST_IGNORE;
        }

        setErrorMessage("");
        setUploading(true);
        void uploadFile(file)
            .then(uploadedFile => {
                if (!uploadedFile?.id) throw new Error("上传响应缺少文件 ID");
                setFileIds(current => [...current, Number(uploadedFile.id)]);
                setFileNames(current => [...current, uploadedFile.name || file.name]);
            })
            .catch(() => setErrorMessage("付款凭证上传失败"))
            .finally(() => setUploading(false));
        return false;
    };

    /** 使用打开弹窗前刷新得到的收款版本提交，满足后端乐观锁字段校验。 */
    const submit = async () => {
        if (!data) return;
        if (!fileIds.length) {
            setErrorMessage("请上传付款凭证");
            return;
        }
        await onSubmit({
            id: data.id,
            fileIds,
            version: data.version,
            paymentInstruction: paymentInstruction || undefined
        });
    };

    return (
        <ZhDialog
            alignCenter
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button aria-label="提交确认" loading={uploading} onClick={() => void submit()} type="primary">
                        确认
                    </Button>
                </Space>
            }
            onCancel={onCancel}
            open={open}
            rootClassName="trade-confirm-payment-dialog-root"
            title="是否要帮买主确认支付信息"
            width={784}
        >
            <div className="trade-confirm-payment-dialog__amount">
                <span>商品金额</span>
                <strong>{Number(data?.amount || 0).toFixed(2)}元</strong>
            </div>
            <div className="trade-confirm-payment-dialog__upload-section">
                <div className="trade-confirm-payment-dialog__upload-label">
                    <Typography.Text>上传支付凭证</Typography.Text>
                    <Typography.Text type="secondary">
                        {`"支持最多上传10个文件，单个文件不超过50M（支持上传压缩包）"`}
                    </Typography.Text>
                </div>
                <Upload.Dragger
                    className="trade-confirm-payment-dialog__dragger"
                    accept={ACCEPTED_EXTENSIONS.join(",")}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                >
                    <i
                        aria-hidden="true"
                        className="iconfont icon-shangchuan trade-confirm-payment-dialog__upload-icon"
                    />
                    <span>上传文件</span>
                </Upload.Dragger>
                {fileNames.map((name, index) => (
                    <Typography.Text className="mr-2" key={`${name}-${index}`}>
                        {name}
                    </Typography.Text>
                ))}
            </div>
            <label className="trade-confirm-payment-dialog__instruction">
                <span>付款说明</span>
                <Input.TextArea
                    aria-label="付款说明"
                    maxLength={200}
                    onChange={event => setPaymentInstruction(event.target.value)}
                    placeholder="请输入付款说明"
                    rows={3}
                    showCount
                    value={paymentInstruction}
                />
            </label>
            {errorMessage ? (
                <Typography.Text className="mt-2 block" role="alert" type="danger">
                    {errorMessage}
                </Typography.Text>
            ) : null}
        </ZhDialog>
    );
}
